import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Activity, 
  Users2, 
  Building2, 
  MessageSquare, 
  RefreshCcw, 
  AlertCircle, 
  ShieldCheck,
  TrendingUp,
  Database,
  Globe2,
  Bell,
  Clock
} from 'lucide-react';

interface SystemStats {
  tenants: {
    total: number;
    active: number;
    inactive: number;
  };
  users: {
    total: number;
    byRole: {
      SUPERADMIN: number;
      TENANT_ADMIN: number;
      USER: number;
    };
  };
  resources: {
    totalContacts: number;
    totalCampaigns: number;
    totalSessions: number;
    totalMessages: number;
  };
  activity: {
    activeToday: number;
    activeCampaigns: number;
    workingSessions: number;
  };
}

interface TenantStats {
  tenantId: string;
  tenantName: string;
  slug: string;
  contactsCount: number;
  campaignsCount: number;
  messagesCount: number;
  activeCampaigns: number;
  workingSessions: number;
  maxContacts: number;
  maxCampaigns: number;
  contactsUsagePct: number;
  campaignsUsagePct: number;
  quotaStatus: string;
  lastContactCreated?: string;
  lastCampaignCreated?: string;
}

interface RecentActivity {
  id: string;
  type: 'tenant_created' | 'user_created' | 'campaign_started' | 'quota_exceeded';
  description: string;
  timestamp: string;
  tenantName?: string;
}

export function SuperAdminDashboard() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [tenantStats, setTenantStats] = useState<TenantStats[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadDashboardData();

    // Auto-refresh a cada 30 segundos
    const interval = setInterval(loadDashboardData, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('auth_token');

      // Carregar estatísticas do sistema
      const [statsResponse, tenantStatsResponse] = await Promise.all([
        fetch('/api/system/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/system/tenant-stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      if (tenantStatsResponse.ok) {
        const tenantStatsData = await tenantStatsResponse.json();
        setTenantStats(tenantStatsData);
      }

      // Simular atividades recentes (em produção viria da API)
      setRecentActivity([
        {
          id: '1',
          type: 'tenant_created',
          description: 'Novo tenant "Empresa ABC" criado',
          timestamp: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
          tenantName: 'Empresa ABC'
        },
        {
          id: '2',
          type: 'campaign_started',
          description: 'Campanha "Promoção Janeiro" iniciada',
          timestamp: new Date(Date.now() - 1000 * 60 * 45).toISOString(),
          tenantName: 'Cliente XYZ'
        },
        {
          id: '3',
          type: 'quota_exceeded',
          description: 'Tenant "Loja Virtual" excedeu quota de contatos',
          timestamp: new Date(Date.now() - 1000 * 60 * 120).toISOString(),
          tenantName: 'Loja Virtual'
        }
      ]);

    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      toast.error('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getQuotaStatusColor = (status: string) => {
    switch (status) {
      case '🔴 Quota exceeded':
        return 'text-red-400 bg-red-500/10 border-red-500/20 shadow-red-900/20';
      case '🟡 Near limit':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20 shadow-yellow-900/20';
      case '🟢 OK':
        return 'text-green-400 bg-green-500/10 border-green-500/20 shadow-green-900/20';
      default:
        return 'text-gray-400 bg-white/5 border-white/10';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'tenant_created':
        return (
          <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20 text-blue-400 shadow-lg shadow-blue-900/20">
            <Building2 className="w-5 h-5" />
          </div>
        );
      case 'campaign_started':
        return (
          <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center border border-green-500/20 text-green-400 shadow-lg shadow-green-900/20">
            <TrendingUp className="w-5 h-5" />
          </div>
        );
      case 'quota_exceeded':
        return (
          <div className="w-10 h-10 bg-red-500/10 rounded-xl flex items-center justify-center border border-red-500/20 text-red-400 shadow-lg shadow-red-900/20">
            <AlertCircle className="w-5 h-5" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 bg-gray-500/10 rounded-xl flex items-center justify-center border border-gray-500/20 text-gray-400">
            <Bell className="w-5 h-5" />
          </div>
        );
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d atrás`;
    if (hours > 0) return `${hours}h atrás`;
    if (minutes > 0) return `${minutes}min atrás`;
    return 'Agora';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] flex items-center justify-center p-8">
        <div className="flex flex-col items-center gap-6 bg-white/5 p-12 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 bg-blue-500/10 rounded-full animate-pulse"></div>
            </div>
          </div>
          <span className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] p-4 sm:p-8 font-oswald">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/5 p-8 rounded-[2.5rem] border border-white/10 backdrop-blur-md shadow-2xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="relative z-10">
            <h1 className="text-4xl font-bold text-white tracking-tight uppercase">
              Dashboard Sistema
            </h1>
            <p className="text-gray-400 mt-2 font-medium tracking-wide uppercase text-xs opacity-80">
              Visão geral do sistema multi-tenant
            </p>
          </div>
          <button
            onClick={loadDashboardData}
            className="relative z-10 px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold font-oswald text-xs uppercase tracking-[0.2em] hover:shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all hover:scale-105 border border-blue-400/50 flex items-center gap-2 group/btn"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
            <RefreshCcw className="w-4 h-4" />
            Atualizar Dados
          </button>
        </div>

        {/* System Overview Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Building2 className="w-16 h-16 text-blue-400" />
              </div>
              <div className="flex flex-col gap-1 relative z-10">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Empresas</p>
                <p className="text-4xl font-bold text-white font-oswald tracking-tight">{stats.tenants.total}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]"></span>
                  <p className="text-[10px] font-bold text-green-400 uppercase tracking-wider">{stats.tenants.active} Ativas</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Users2 className="w-16 h-16 text-purple-400" />
              </div>
              <div className="flex flex-col gap-1 relative z-10">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Usuários</p>
                <p className="text-4xl font-bold text-white font-oswald tracking-tight">{stats.users.total}</p>
                <p className="text-[10px] font-bold text-purple-400 uppercase tracking-wider mt-2">
                  {stats.users.byRole.TENANT_ADMIN} Admins
                </p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <MessageSquare className="w-16 h-16 text-green-400" />
              </div>
              <div className="flex flex-col gap-1 relative z-10">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Contatos</p>
                <p className="text-4xl font-bold text-white font-oswald tracking-tight">
                  {stats.resources.totalContacts.toLocaleString()}
                </p>
                <p className="text-[10px] font-bold text-green-400 uppercase tracking-wider mt-2">Total no Sistema</p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] p-8 shadow-2xl hover:bg-white/10 transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Activity className="w-16 h-16 text-orange-400" />
              </div>
              <div className="flex flex-col gap-1 relative z-10">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Campanhas</p>
                <p className="text-4xl font-bold text-white font-oswald tracking-tight">{stats.resources.totalCampaigns}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]"></span>
                  <p className="text-[10px] font-bold text-green-400 uppercase tracking-wider">{stats.activity.activeCampaigns} Ativas</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Tenant Stats Table */}
          <div className="lg:col-span-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] overflow-hidden shadow-2xl">
            <div className="px-10 py-8 border-b border-white/10 bg-white/5">
              <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                Status das Empresas
              </h2>
            </div>
            <div className="overflow-x-auto custom-scrollbar">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Tenant</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Recursos</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Uso Quotas</th>
                    <th className="px-10 py-6 text-left text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] font-oswald">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {tenantStats.map((tenant) => (
                    <tr key={tenant.tenantId} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-10 py-6 whitespace-nowrap">
                        <div className="text-sm font-bold text-white uppercase tracking-wide font-oswald">{tenant.tenantName}</div>
                        <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider font-oswald mt-1">{tenant.slug}</div>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <div className="space-y-1 text-[10px] font-bold uppercase tracking-wider font-oswald">
                          <div className="text-blue-400">{tenant.contactsCount} Contatos</div>
                          <div className="text-purple-400">{tenant.campaignsCount} Campanhas</div>
                          <div className="text-green-400">{tenant.messagesCount} Mensagens</div>
                        </div>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <div className="space-y-3 w-40">
                          <div className="space-y-1">
                            <div className="flex justify-between text-[8px] font-bold text-gray-400 uppercase tracking-widest">
                              <span>Contatos</span>
                              <span>{tenant.contactsUsagePct}%</span>
                            </div>
                            <div className="w-full bg-white/5 rounded-full h-1.5 border border-white/5">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  tenant.contactsUsagePct >= 90 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' :
                                  tenant.contactsUsagePct >= 70 ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.4)]' : 
                                  'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                                }`}
                                style={{ width: `${Math.min(tenant.contactsUsagePct, 100)}%` }}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-[8px] font-bold text-gray-400 uppercase tracking-widest">
                              <span>Campanhas</span>
                              <span>{tenant.campaignsUsagePct}%</span>
                            </div>
                            <div className="w-full bg-white/5 rounded-full h-1.5 border border-white/5">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${
                                  tenant.campaignsUsagePct >= 90 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' :
                                  tenant.campaignsUsagePct >= 70 ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.4)]' : 
                                  'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]'
                                }`}
                                style={{ width: `${Math.min(tenant.campaignsUsagePct, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-10 py-6 whitespace-nowrap">
                        <span className={`inline-flex px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] rounded-full border shadow-sm ${getQuotaStatusColor(tenant.quotaStatus)}`}>
                          {tenant.quotaStatus.replace(/[🔴🟡🟢]/gu, '').trim()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] shadow-2xl p-10">
            <div className="mb-10">
              <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
                <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
                Atividade Recente
              </h2>
            </div>
            <div className="space-y-8">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start gap-5 group">
                  {getActivityIcon(activity.type)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white uppercase tracking-wide font-oswald group-hover:text-blue-400 transition-colors">
                      {activity.description}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      {activity.tenantName && (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                          <Building2 className="w-3 h-3" />
                          {activity.tenantName}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
                        <Clock className="w-3 h-3" />
                        {formatTimeAgo(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {recentActivity.length === 0 && (
                <div className="text-center py-12 bg-white/5 rounded-[2rem] border border-white/5">
                  <Bell className="mx-auto h-12 w-12 text-gray-700 mb-4" />
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-[0.2em]">Nenhuma atividade recente</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* System Health Indicators */}
        <div className="mt-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-[2.5rem] shadow-2xl p-10">
          <div className="mb-10">
            <h2 className="text-xl font-bold text-white font-oswald uppercase tracking-tight flex items-center gap-3">
              <span className="w-2 h-6 bg-blue-600 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)]"></span>
              Saúde do Sistema
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5 text-center hover:bg-white/10 transition-all group">
              <div className="w-20 h-20 bg-green-500/10 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-green-500/20 shadow-lg shadow-green-900/20 group-hover:scale-110 transition-transform">
                <ShieldCheck className="w-10 h-10 text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-white font-oswald uppercase tracking-tight">Sistema Online</h3>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-2">Todos os serviços estáveis</p>
            </div>

            <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5 text-center hover:bg-white/10 transition-all group">
              <div className="w-20 h-20 bg-blue-500/10 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-blue-500/20 shadow-lg shadow-blue-900/20 group-hover:scale-110 transition-transform">
                <Database className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white font-oswald uppercase tracking-tight">Banco de Dados</h3>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-2">Conexão estável</p>
            </div>

            <div className="bg-white/5 rounded-[2rem] p-8 border border-white/5 text-center hover:bg-white/10 transition-all group">
              <div className="w-20 h-20 bg-purple-500/10 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-purple-500/20 shadow-lg shadow-purple-900/20 group-hover:scale-110 transition-transform">
                <Globe2 className="w-10 h-10 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white font-oswald uppercase tracking-tight">WhatsApp</h3>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-2">
                {stats ? `${stats.activity.workingSessions} Sessões Ativas` : 'Monitorando...'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}